import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowRight, Mail, Loader2, CheckCircle, Sparkles, Play, Video, Clock, BookOpen, Lock, ChevronDown, Gift } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import WealthsimpleStepsModal from "@/components/WealthsimpleStepsModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Function to clean AI-generated content by removing citation brackets like [1], {4}, etc.
const cleanContent = (content) => {
  if (!content) return content;
  return content
    .replace(/\[\d+\]/g, '')
    .replace(/\{\d+\}/g, '')
    .replace(/\(\d+\)/g, '')
    .replace(/【\d+】/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const categories = [
  "All",
  "TFSA Basics",
  "Beginner Investing",
  "Student Budgeting",
  "Canada Money",
];

// Video learning content from the team - Micro-Courses
const microCourses = [
  {
    id: "course-1",
    title: "Understanding P/E Ratio",
    subtitle: "Stock Valuation Fundamentals",
    description: "Learn what the Price-to-Earnings ratio means and how to use it to evaluate whether a stock is overvalued or undervalued. Essential knowledge for any investor.",
    duration: "0:47",
    category: "Fundamentals",
    level: "Beginner",
    videoUrl: "https://customer-assets.emergentagent.com/job_finedge-tools/artifacts/3j89o096_Video%201.mp4",
    thumbnail: "https://customer-assets.emergentagent.com/job_fintoolkit/artifacts/hnuyo6z0_image.png",
    learningOutcomes: [
      "Understand what P/E ratio represents",
      "Compare P/E ratios across companies",
      "Identify overvalued vs undervalued stocks",
      "Apply P/E analysis to your investments"
    ],
    relatedBlog: {
      title: "P/E Ratio Explained: A Beginner's Guide to Stock Valuation",
      excerpt: "The Price-to-Earnings (P/E) ratio is one of the most important metrics for evaluating stocks. Here's everything you need to know about using it effectively.",
      content: `
        <h2>What is the P/E Ratio?</h2>
        <p>The Price-to-Earnings ratio compares a company's stock price to its earnings per share (EPS). It tells you how much investors are willing to pay for each dollar of earnings.</p>
        
        <h3>The Formula</h3>
        <p><strong>P/E Ratio = Stock Price ÷ Earnings Per Share</strong></p>
        <p>For example, if a stock trades at $50 and earns $5 per share, its P/E ratio is 10.</p>
        
        <h3>What Does It Mean?</h3>
        <ul>
          <li><strong>Low P/E (under 15):</strong> May indicate undervaluation or slow growth expectations</li>
          <li><strong>Average P/E (15-25):</strong> Typically seen in mature, stable companies</li>
          <li><strong>High P/E (over 25):</strong> Suggests high growth expectations or potential overvaluation</li>
        </ul>
        
        <h3>How to Use P/E Ratio</h3>
        <ol>
          <li><strong>Compare within industries:</strong> A tech company's P/E shouldn't be compared to a utility company</li>
          <li><strong>Look at historical P/E:</strong> Is the current ratio higher or lower than the company's average?</li>
          <li><strong>Consider growth rate:</strong> Fast-growing companies often deserve higher P/E ratios</li>
          <li><strong>Don't rely on it alone:</strong> Use alongside other metrics like P/B ratio and debt levels</li>
        </ol>
        
        <h3>Real-World Example</h3>
        <p>If Company A has a P/E of 10 and Company B has a P/E of 30, Company B isn't necessarily a worse investment. Company B might be growing much faster, justifying the premium.</p>
        
        <h3>Key Takeaway</h3>
        <p>The P/E ratio is a starting point, not the final answer. Use it to screen stocks and identify opportunities, then dig deeper into the company's fundamentals before investing.</p>
      `,
      readingTime: "4 min read"
    }
  },
  {
    id: "course-2", 
    title: "ETFs Explained",
    subtitle: "Your First Investment Vehicle",
    description: "Everything you need to know about Exchange-Traded Funds (ETFs). The easiest and most diversified way to start investing in the stock market.",
    duration: "0:52",
    category: "ETF Basics",
    level: "Beginner",
    videoUrl: "https://customer-assets.emergentagent.com/job_finedge-tools/artifacts/ndvup1ve_AI%20captions%20-%20Video%202.mp4%20%281%29.mp4",
    thumbnail: "https://customer-assets.emergentagent.com/job_fintoolkit/artifacts/8od5e0dv_Gemini_Generated_Image_mgmo0nmgmo0nmgmo.png",
    learningOutcomes: [
      "Understand what ETFs are and how they work",
      "Compare ETFs to mutual funds and individual stocks",
      "Learn about different types of ETFs",
      "Know how to buy your first ETF"
    ],
    relatedBlog: {
      title: "ETFs 101: The Ultimate Beginner's Guide to Exchange-Traded Funds",
      excerpt: "ETFs have revolutionized investing for beginners. Learn why they're the perfect starting point for building your portfolio.",
      content: `
        <h2>What is an ETF?</h2>
        <p>An Exchange-Traded Fund (ETF) is a basket of securities that trades on a stock exchange, just like a regular stock. When you buy one share of an ETF, you're buying a tiny piece of all the companies inside it.</p>
        
        <h3>Why ETFs Are Perfect for Beginners</h3>
        <ul>
          <li><strong>Instant diversification:</strong> One ETF can hold hundreds of stocks</li>
          <li><strong>Low cost:</strong> Most ETFs have fees under 0.5% per year</li>
          <li><strong>Easy to buy:</strong> Trade them like stocks on any brokerage</li>
          <li><strong>Transparent:</strong> You can see exactly what's inside</li>
        </ul>
        
        <h3>Types of ETFs</h3>
        <ol>
          <li><strong>Index ETFs:</strong> Track major indices like the S&P 500 (e.g., VOO, SPY)</li>
          <li><strong>Sector ETFs:</strong> Focus on specific industries (tech, healthcare, energy)</li>
          <li><strong>Bond ETFs:</strong> Invest in government or corporate bonds</li>
          <li><strong>International ETFs:</strong> Exposure to global markets</li>
          <li><strong>All-in-One ETFs:</strong> Complete portfolios in one fund (e.g., XEQT, VGRO)</li>
        </ol>
        
        <h3>Popular Canadian ETFs for Beginners</h3>
        <ul>
          <li><strong>XEQT:</strong> 100% global equities, one fund for everything</li>
          <li><strong>VGRO:</strong> 80% stocks, 20% bonds for a balanced approach</li>
          <li><strong>VFV:</strong> Tracks the S&P 500 in Canadian dollars</li>
          <li><strong>XIC:</strong> Tracks the Canadian stock market</li>
        </ul>
        
        <h3>ETFs vs. Mutual Funds</h3>
        <table>
          <tr><th>Feature</th><th>ETFs</th><th>Mutual Funds</th></tr>
          <tr><td>Trading</td><td>Anytime during market hours</td><td>Once per day</td></tr>
          <tr><td>Fees (MER)</td><td>Usually 0.03% - 0.5%</td><td>Often 1% - 2.5%</td></tr>
          <tr><td>Minimum</td><td>Price of one share</td><td>Often $500+</td></tr>
        </table>
        
        <h3>How to Buy Your First ETF</h3>
        <ol>
          <li>Open a brokerage account (Wealthsimple, Questrade)</li>
          <li>Decide on your risk tolerance</li>
          <li>Choose an all-in-one ETF or build your own mix</li>
          <li>Place a market or limit order</li>
          <li>Hold for the long term and keep adding</li>
        </ol>
        
        <h3>Key Takeaway</h3>
        <p>ETFs are the simplest way to build a diversified portfolio. Start with an all-in-one ETF like XEQT or VGRO, contribute regularly, and let compound growth do the work.</p>
      `,
      readingTime: "5 min read"
    }
  },
  {
    id: "course-3",
    title: "Understanding Sectors",
    subtitle: "Navigate the Market by Industry",
    description: "Learn how the stock market is divided into sectors and why understanding them is crucial for building a diversified portfolio and spotting investment opportunities.",
    duration: "1:00",
    category: "Market Structure",
    level: "Beginner",
    videoUrl: "https://customer-assets.emergentagent.com/job_fintoolkit/artifacts/3zkpxssy_VIDEO-2026-02-07-21-23-30.mp4",
    thumbnail: "https://customer-assets.emergentagent.com/job_fintoolkit/artifacts/8olexr1f_Understanding%20Sectors%20%281%29.png",
    learningOutcomes: [
      "Understand the 11 major market sectors",
      "Learn which sectors perform well in different economic conditions",
      "Identify sector rotation strategies",
      "Diversify your portfolio across sectors"
    ],
    relatedBlog: {
      title: "Stock Market Sectors Explained: A Complete Guide for Beginners",
      excerpt: "Understanding market sectors helps you diversify your portfolio and capitalize on economic trends. Here's everything you need to know.",
      content: `
        <h2>What Are Market Sectors?</h2>
        <p>The stock market is divided into 11 sectors based on what companies do. Each sector groups companies with similar business activities, making it easier to analyze and compare investments.</p>
        
        <h3>The 11 Market Sectors</h3>
        <ol>
          <li><strong>Technology:</strong> Software, hardware, semiconductors (Apple, Microsoft, NVIDIA)</li>
          <li><strong>Healthcare:</strong> Pharmaceuticals, biotech, medical devices (Johnson & Johnson, Pfizer)</li>
          <li><strong>Financials:</strong> Banks, insurance, asset managers (JPMorgan, Visa, Berkshire)</li>
          <li><strong>Consumer Discretionary:</strong> Retail, entertainment, automobiles (Amazon, Tesla, Nike)</li>
          <li><strong>Consumer Staples:</strong> Food, beverages, household products (Coca-Cola, P&G, Walmart)</li>
          <li><strong>Energy:</strong> Oil, gas, renewable energy (ExxonMobil, Chevron, Enbridge)</li>
          <li><strong>Industrials:</strong> Manufacturing, aerospace, transportation (Boeing, Caterpillar, UPS)</li>
          <li><strong>Materials:</strong> Chemicals, metals, mining (Dow, Newmont, BHP)</li>
          <li><strong>Real Estate:</strong> REITs, property developers (Prologis, American Tower)</li>
          <li><strong>Utilities:</strong> Electric, gas, water services (NextEra, Duke Energy)</li>
          <li><strong>Communication Services:</strong> Telecom, media, internet (Google, Meta, Netflix)</li>
        </ol>
        
        <h3>Sector Performance in Different Economic Conditions</h3>
        <ul>
          <li><strong>Economic Growth:</strong> Technology, Consumer Discretionary, and Financials tend to outperform</li>
          <li><strong>Recession:</strong> Consumer Staples, Healthcare, and Utilities are defensive plays</li>
          <li><strong>Inflation:</strong> Energy, Materials, and Real Estate often provide protection</li>
          <li><strong>Rising Interest Rates:</strong> Financials benefit; Real Estate and Utilities may struggle</li>
        </ul>
        
        <h3>Sector ETFs for Easy Investing</h3>
        <ul>
          <li><strong>XLK:</strong> Technology Select Sector SPDR</li>
          <li><strong>XLF:</strong> Financial Select Sector SPDR</li>
          <li><strong>XLV:</strong> Health Care Select Sector SPDR</li>
          <li><strong>XLE:</strong> Energy Select Sector SPDR</li>
        </ul>
        
        <h3>Key Takeaway</h3>
        <p>Don't put all your eggs in one sector. A diversified portfolio spans multiple sectors to reduce risk and capture growth across different parts of the economy.</p>
      `,
      readingTime: "5 min read"
    }
  },
  {
    id: "course-4",
    title: "ETF Deep Dive",
    subtitle: "Advanced ETF Strategies",
    description: "Go beyond the basics and learn advanced ETF concepts including expense ratios, tracking error, and how to build a complete portfolio using ETFs.",
    duration: "1:00",
    category: "ETF Basics",
    level: "Intermediate",
    videoUrl: "https://customer-assets.emergentagent.com/job_fintoolkit/artifacts/pot8f4t7_VIDEO-2026-02-07-21-24-43.mp4",
    thumbnail: "https://customer-assets.emergentagent.com/job_fintoolkit/artifacts/ld2h7uj5_Gemini_Generated_Image_y47sxjy47sxjy47s.png",
    learningOutcomes: [
      "Understand ETF expense ratios and their impact",
      "Learn about tracking error and index replication",
      "Compare accumulating vs distributing ETFs",
      "Build a complete portfolio using only ETFs"
    ],
    relatedBlog: {
      title: "Advanced ETF Strategies: Building Your Perfect Portfolio",
      excerpt: "Ready to take your ETF knowledge to the next level? Learn the advanced concepts that separate amateur investors from pros.",
      content: `
        <h2>Beyond the Basics: Advanced ETF Concepts</h2>
        <p>Once you understand what ETFs are, it's time to learn how to evaluate them like a pro and build an optimal portfolio.</p>
        
        <h3>Understanding Expense Ratios (MER)</h3>
        <p>The Management Expense Ratio (MER) is the annual fee you pay to own an ETF. It's expressed as a percentage and automatically deducted from the fund's returns.</p>
        <ul>
          <li><strong>Low-cost index ETFs:</strong> 0.03% - 0.20% (e.g., VFV at 0.08%)</li>
          <li><strong>Sector/thematic ETFs:</strong> 0.20% - 0.75%</li>
          <li><strong>Active ETFs:</strong> 0.50% - 1.00%+</li>
        </ul>
        <p><strong>Example:</strong> On a $10,000 investment, a 0.20% MER costs $20/year, while a 1.5% mutual fund costs $150/year.</p>
        
        <h3>Tracking Error</h3>
        <p>Tracking error measures how closely an ETF follows its benchmark index. Lower is better.</p>
        <ul>
          <li><strong>Causes:</strong> Trading costs, cash drag, sampling methods</li>
          <li><strong>Good tracking:</strong> Under 0.10% annually</li>
          <li><strong>Red flag:</strong> Consistently underperforming by 0.50%+</li>
        </ul>
        
        <h3>Accumulating vs Distributing ETFs</h3>
        <ul>
          <li><strong>Distributing:</strong> Pays dividends to your account (common in Canada/US)</li>
          <li><strong>Accumulating:</strong> Automatically reinvests dividends (tax-efficient in some countries)</li>
        </ul>
        
        <h3>Building a Complete ETF Portfolio</h3>
        <p><strong>Simple 3-Fund Portfolio:</strong></p>
        <ol>
          <li>Canadian stocks (XIC) - 25%</li>
          <li>US stocks (VFV) - 45%</li>
          <li>International stocks (XEF) - 30%</li>
        </ol>
        <p><strong>Or use an all-in-one:</strong> XEQT (100% equity) or VGRO (80/20 stocks/bonds)</p>
        
        <h3>Key Takeaway</h3>
        <p>Focus on low MER, good tracking, and proper diversification. For most investors, a single all-in-one ETF like XEQT provides everything you need.</p>
      `,
      readingTime: "6 min read"
    }
  },
  {
    id: "course-5",
    title: "Market Cap Explained",
    subtitle: "Size Matters in Investing",
    description: "Understand market capitalization: how companies are classified by size and why it matters for your investment strategy and risk management.",
    duration: "1:00",
    category: "Fundamentals",
    level: "Beginner",
    videoUrl: "https://customer-assets.emergentagent.com/job_fintoolkit/artifacts/wzkozvgr_VIDEO-2026-02-07-21-29-17.mp4",
    thumbnail: "https://customer-assets.emergentagent.com/job_fintoolkit/artifacts/9cqezp6i_mkt%20cap.png",
    learningOutcomes: [
      "Calculate and understand market capitalization",
      "Differentiate between large-cap, mid-cap, and small-cap stocks",
      "Learn the risk/reward tradeoffs of different market caps",
      "Build a balanced portfolio across market caps"
    ],
    relatedBlog: {
      title: "Market Cap 101: Why Company Size Matters for Investors",
      excerpt: "Market capitalization tells you how big a company is. Learn why this simple metric is crucial for building a balanced portfolio.",
      content: `
        <h2>What is Market Capitalization?</h2>
        <p>Market cap (market capitalization) is the total value of a company's outstanding shares. It's calculated by multiplying the stock price by the number of shares.</p>
        <p><strong>Formula:</strong> Market Cap = Stock Price × Shares Outstanding</p>
        
        <h3>Market Cap Categories</h3>
        <ul>
          <li><strong>Mega-cap:</strong> $200 billion+ (Apple, Microsoft, Amazon)</li>
          <li><strong>Large-cap:</strong> $10 - $200 billion (Nike, Starbucks, AMD)</li>
          <li><strong>Mid-cap:</strong> $2 - $10 billion (established growth companies)</li>
          <li><strong>Small-cap:</strong> $300 million - $2 billion (emerging companies)</li>
          <li><strong>Micro-cap:</strong> Under $300 million (high risk, high potential)</li>
        </ul>
        
        <h3>Risk and Return by Market Cap</h3>
        <table>
          <tr><th>Category</th><th>Risk Level</th><th>Growth Potential</th><th>Volatility</th></tr>
          <tr><td>Large-cap</td><td>Lower</td><td>Moderate</td><td>Lower</td></tr>
          <tr><td>Mid-cap</td><td>Medium</td><td>Higher</td><td>Medium</td></tr>
          <tr><td>Small-cap</td><td>Higher</td><td>Highest</td><td>Higher</td></tr>
        </table>
        
        <h3>Why Market Cap Matters</h3>
        <ol>
          <li><strong>Stability:</strong> Large-caps are typically more stable and pay dividends</li>
          <li><strong>Growth:</strong> Small-caps have more room to grow but are riskier</li>
          <li><strong>Index inclusion:</strong> S&P 500 only includes large-caps</li>
          <li><strong>Liquidity:</strong> Larger companies have more trading volume</li>
        </ol>
        
        <h3>Market Cap ETFs</h3>
        <ul>
          <li><strong>Large-cap:</strong> SPY, VOO (S&P 500)</li>
          <li><strong>Mid-cap:</strong> IJH, VO (Vanguard Mid-Cap)</li>
          <li><strong>Small-cap:</strong> IJR, VB (Vanguard Small-Cap)</li>
          <li><strong>Total market:</strong> VTI (all sizes combined)</li>
        </ul>
        
        <h3>Key Takeaway</h3>
        <p>A diversified portfolio includes companies of different sizes. Large-caps provide stability, while small and mid-caps offer growth potential. Consider your risk tolerance when choosing the right mix.</p>
      `,
      readingTime: "5 min read"
    }
  },
];

// Micro-Course Card Component - Premium Learning Experience
const MicroCourseCard = ({ course, onWatchVideo, expanded, onToggleExpand }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="mb-8"
    >
      <Card className="border-0 shadow-xl overflow-hidden bg-white">
        {/* Course Header */}
        <div className="bg-gradient-to-r from-navy-700 to-navy-800 p-6 text-white">
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-emerald-500 text-white border-0">
              {course.level}
            </Badge>
            <Badge className="bg-white/20 text-white border-0">
              {course.category}
            </Badge>
          </div>
          <h3 className="text-2xl font-heading font-bold mb-1">{course.title}</h3>
          <p className="text-white/80 text-sm">{course.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-0">
          {/* Video Section */}
          <div className="relative">
            <div 
              className="relative h-64 lg:h-full min-h-[250px] cursor-pointer group"
              onClick={onWatchVideo}
            >
              {course.thumbnail ? (
                <img 
                  src={course.thumbnail} 
                  alt={course.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-emerald-600 to-teal-700" />
              )}
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-white shadow-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play className="w-8 h-8 text-navy-700 ml-1" />
                </div>
              </div>
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <Badge className="bg-black/70 text-white border-0 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {course.duration}
                </Badge>
                <Badge className="bg-emerald-500 text-white border-0">
                  Watch Lesson
                </Badge>
              </div>
            </div>
          </div>

          {/* Course Info */}
          <div className="p-6">
            <p className="text-slate-600 mb-4">{course.description}</p>
            
            <div className="mb-4">
              <h4 className="font-semibold text-navy-700 mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                What You&apos;ll Learn
              </h4>
              <ul className="space-y-2">
                {course.learningOutcomes.map((outcome, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 shrink-0" />
                    {outcome}
                  </li>
                ))}
              </ul>
            </div>

            <Button 
              onClick={onWatchVideo}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              <Play className="w-4 h-4 mr-2" />
              Start Learning
            </Button>
          </div>
        </div>

        {/* Related Blog Content */}
        <div className="border-t bg-slate-50">
          <button
            onClick={onToggleExpand}
            className="w-full p-4 flex items-center justify-between hover:bg-slate-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-navy-700 text-sm">Continue Reading</p>
                <p className="text-slate-500 text-xs">{course.relatedBlog.title}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-slate-200 text-slate-600 text-xs">
                {course.relatedBlog.readingTime}
              </Badge>
              <ArrowRight className={`w-5 h-5 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            </div>
          </button>

          {/* Expanded Blog Content */}
          {expanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="px-6 pb-6"
            >
              <div className="bg-white rounded-xl p-6 shadow-inner border">
                <h4 className="text-xl font-heading font-bold text-navy-700 mb-2">
                  {course.relatedBlog.title}
                </h4>
                <p className="text-emerald-600 text-sm mb-4">{course.relatedBlog.excerpt}</p>
                <div 
                  className="prose prose-slate prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: course.relatedBlog.content }}
                />
              </div>
            </motion.div>
          )}
        </div>
      </Card>
    </motion.div>
  );
};

// Keep original VideoCard for backward compatibility if needed
const VideoCard = ({ video, onClick }) => {
  const gradients = [
    "from-purple-600 to-indigo-700",
    "from-emerald-600 to-teal-700",
    "from-blue-600 to-cyan-700",
    "from-orange-500 to-red-600",
  ];
  const gradient = gradients[Math.abs(video.id.charCodeAt(video.id.length - 1)) % gradients.length];

  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -5 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="cursor-pointer"
    >
      <Card className="h-full border-0 shadow-card overflow-hidden bg-white group">
        {/* Thumbnail / Video Preview */}
        <div className="relative h-48 flex items-center justify-center overflow-hidden">
          {video.thumbnail ? (
            <img 
              src={video.thumbnail} 
              alt={video.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className={`w-full h-full bg-gradient-to-br ${gradient}`} />
          )}
          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
              <Play className="w-7 h-7 text-slate-800 ml-1" />
            </div>
          </div>
          {/* Duration badge */}
          <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {video.duration}
          </div>
          {/* Category badge */}
          <div className="absolute top-3 left-3">
            <Badge className="bg-white/90 text-slate-700 text-xs">
              {video.category}
            </Badge>
          </div>
        </div>
        <CardContent className="pt-4">
          <h3 className="font-heading font-bold text-lg text-navy-700 mb-2 group-hover:text-emerald-600 transition-colors">
            {video.title}
          </h3>
          <p className="text-slate-600 text-sm line-clamp-2">
            {video.description}
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Video Modal Component
const VideoModal = ({ video, onClose }) => {
  const [videoError, setVideoError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  if (!video) return null;

  const handleVideoError = () => {
    setVideoError(true);
    setIsLoading(false);
  };

  const handleVideoLoad = () => {
    setIsLoading(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl overflow-hidden max-w-4xl w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Video Player */}
        <div className="relative aspect-video bg-black">
          {isLoading && !videoError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
            </div>
          )}
          {videoError ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4">
              <Play className="w-16 h-16 mb-4 opacity-50" />
              <p className="text-lg mb-4 text-center">Video cannot be played directly in the browser</p>
              <a 
                href={video.videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Open Video in New Tab
              </a>
              <p className="text-sm text-gray-400 mt-3">The video will open in a new browser tab</p>
            </div>
          ) : (
            <video
              src={video.videoUrl}
              controls
              autoPlay
              playsInline
              className="w-full h-full"
              onError={handleVideoError}
              onLoadedData={handleVideoLoad}
              onCanPlay={handleVideoLoad}
            >
              Your browser does not support the video tag.
            </video>
          )}
        </div>
        {/* Video Info */}
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Badge className="bg-emerald-100 text-emerald-700 mb-2">
                {video.category}
              </Badge>
              <h3 className="font-heading font-bold text-xl text-navy-700 mb-2">
                {video.title}
              </h3>
              <p className="text-slate-600">
                {video.description}
              </p>
            </div>
            <Button variant="ghost" onClick={onClose} className="flex-shrink-0">
              Close
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [wealthsimpleModalOpen, setWealthsimpleModalOpen] = useState(false);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/blog`);
        setPosts(response.data);
        setFilteredPosts(response.data);
      } catch (error) {
        console.error("Error fetching posts:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  useEffect(() => {
    let filtered = posts;

    if (activeCategory !== "All") {
      filtered = filtered.filter((post) => post.category === activeCategory);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (post) =>
          post.title.toLowerCase().includes(query) ||
          post.excerpt.toLowerCase().includes(query)
      );
    }

    setFilteredPosts(filtered);
  }, [activeCategory, searchQuery, posts]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="blog-page">
      {/* Header */}
      <section className="py-16 lg:py-24 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link to="/" className="inline-flex items-center text-emerald-600 hover:text-emerald-700 mb-6 text-sm font-medium">
            ← Back to Home
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge className="bg-emerald-100 text-emerald-700 mb-4">
              Learn & Grow
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-navy-700 mb-4">
              The Compoundr Blog
            </h1>
            <p className="text-lg text-slate-600">
              Clear, actionable investing content for Canadian students.
              No jargon, no hype. Just knowledge that helps.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Investing 101 Series - Featured Course */}
      <section className="py-16 bg-gradient-to-br from-navy-700 via-navy-800 to-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Badge className="bg-emerald-500 text-white mb-4">
              <BookOpen className="w-3 h-3 mr-1" />
              Complete Course
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              Starting to Invest in Canada: The Complete Beginner&apos;s Guide
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-6">
              A 5-part blog series that teaches you everything you need to know about investing. 
              Written for complete beginners. No jargon, just real knowledge.
            </p>
            <div className="flex flex-wrap justify-center gap-3 text-sm text-slate-400 mb-8">
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> ~1 hour total read</span>
              <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-emerald-400" /> 5 comprehensive parts</span>
              <span className="flex items-center gap-1"><Sparkles className="w-4 h-4 text-yellow-400" /> Completely free</span>
            </div>
          </div>

          {/* Series Cards */}
          <div className="grid md:grid-cols-5 gap-4">
            {[
              {
                part: 1,
                title: "Why Your Money Needs to Make Money",
                subtitle: "Inflation & the power of investing",
                slug: "investing-101-why-your-money-needs-to-make-money",
                color: "from-rose-500 to-rose-600"
              },
              {
                part: 2,
                title: "Opening a Brokerage Account & TFSAs",
                subtitle: "Your first practical steps",
                slug: "investing-101-opening-brokerage-account-tfsa",
                color: "from-orange-500 to-orange-600"
              },
              {
                part: 3,
                title: "Stocks, Bonds, and ETFs Explained",
                subtitle: "What to actually buy",
                slug: "investing-101-stocks-bonds-etfs-explained",
                color: "from-amber-500 to-amber-600"
              },
              {
                part: 4,
                title: "The Magic of Compound Interest",
                subtitle: "How $200/month becomes $500K",
                slug: "investing-101-compound-interest-magic",
                color: "from-emerald-500 to-emerald-600"
              },
              {
                part: 5,
                title: "Building Your Portfolio",
                subtitle: "TFSA vs RRSP decoded",
                slug: "investing-101-building-portfolio-tfsa-vs-rrsp",
                color: "from-blue-500 to-blue-600"
              }
            ].map((item) => (
              <Link 
                key={item.part}
                to={`/blog/${item.slug}`}
                className="group"
              >
                <Card className="bg-white/10 backdrop-blur border-white/20 hover:bg-white/20 transition-all h-full">
                  <CardContent className="p-4">
                    <div className={`w-8 h-8 rounded-full bg-gradient-to-r ${item.color} flex items-center justify-center text-white font-bold text-sm mb-3`}>
                      {item.part}
                    </div>
                    <h3 className="font-semibold text-white text-sm mb-1 group-hover:text-emerald-300 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-xs text-slate-400">{item.subtitle}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {/* Start Reading CTA */}
          <div className="text-center mt-8">
            <Link to="/blog/investing-101-why-your-money-needs-to-make-money">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg">
                Start Reading Part 1 <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>

          {/* PDF Guide Teaser */}
          <div className="mt-12 bg-white/5 rounded-2xl p-6 border border-white/10">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-shrink-0">
                <div className="w-20 h-24 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-lg shadow-lg flex items-center justify-center">
                  <BookOpen className="w-10 h-10 text-white" />
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl font-bold text-white mb-2">
                  Free PDF Guide: Everything About Investing Explained Like You&apos;re 10
                </h3>
                <p className="text-slate-300 mb-4">
                  36-page comprehensive guide covering everything from inflation to building your first portfolio. 
                  Perfect for printing or reading offline.
                </p>
                <Button 
                  variant="outline" 
                  className="border-emerald-400 text-emerald-400 hover:bg-emerald-400 hover:text-white"
                  onClick={() => window.open('/api/guide/download', '_blank')}
                >
                  Download Free PDF Guide
                </Button>
              </div>
            </div>
          </div>

          {/* Wealthsimple $25 Offer */}
          <div className="mt-6 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-2xl p-6 border border-yellow-500/30">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full shadow-lg flex items-center justify-center">
                  <Gift className="w-8 h-8 text-white" />
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <Badge className="bg-yellow-500/20 text-yellow-300 mb-2">
                  <Sparkles className="w-3 h-3 mr-1" />
                  Don&apos;t Forget!
                </Badge>
                <h3 className="text-xl font-bold text-white mb-2">
                  Get Free $25 When You Open a Wealthsimple TFSA
                </h3>
                <p className="text-slate-300 mb-4">
                  Ready to start investing? Open your TFSA with Wealthsimple and get $25 free towards your first investment. 
                  It takes just 10 minutes and you can start with any amount.
                </p>
                <Button 
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold shadow-lg"
                  onClick={() => setWealthsimpleModalOpen(true)}
                >
                  <Gift className="w-4 h-4 mr-2" />
                  Claim Free $25 →
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Micro-Courses Section - Visual Thumbnail Format */}
      <section className="py-12 bg-gradient-to-b from-slate-100 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
              <Video className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-bold text-navy-700">
                Mini Video Lessons
              </h2>
              <p className="text-sm text-slate-500">
                {microCourses.length} bite-sized lessons • Click to expand
              </p>
            </div>
          </div>
          
          {/* Video Lessons with Thumbnails */}
          <div className="space-y-3">
            {microCourses.map((course, index) => (
              <motion.div 
                key={course.id} 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Lesson Header - Clickable with Thumbnail */}
                <button
                  onClick={() => setExpandedCourse(expandedCourse === course.id ? null : course.id)}
                  className="w-full flex items-center gap-4 p-3 hover:bg-slate-50/50 transition-colors text-left group"
                >
                  {/* Thumbnail with Play Overlay */}
                  <div className="relative flex-shrink-0">
                    <div className="w-24 h-16 sm:w-32 sm:h-20 rounded-lg overflow-hidden bg-slate-200">
                      <img 
                        src={course.thumbnail} 
                        alt={course.title}
                        className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                      />
                    </div>
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center group-hover:bg-emerald-500 transition-colors">
                        <Play className="w-4 h-4 sm:w-5 sm:h-5 text-white fill-white ml-0.5" />
                      </div>
                    </div>
                    {/* Duration Badge */}
                    <div className="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                      {course.duration}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-semibold text-navy-700 group-hover:text-emerald-600 transition-colors line-clamp-1">
                          {course.title}
                        </h3>
                        <p className="text-sm text-slate-500 line-clamp-1 hidden sm:block">
                          {course.subtitle}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge className="bg-slate-100 text-slate-600 text-xs hidden md:flex">
                          {course.category}
                        </Badge>
                        <motion.div
                          animate={{ rotate: expandedCourse === course.id ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        </motion.div>
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-1 line-clamp-2 sm:line-clamp-1">
                      {course.description}
                    </p>
                  </div>
                </button>
                
                {/* Expanded Content */}
                <AnimatePresence>
                  {expandedCourse === course.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 pt-0 border-t border-slate-100">
                        <div className="flex flex-col sm:flex-row gap-4 pt-4">
                          {/* Left: Learning Outcomes */}
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-slate-500 uppercase mb-3">What You&apos;ll Learn</p>
                            <ul className="space-y-2">
                              {course.learningOutcomes?.slice(0, 4).map((outcome, i) => (
                                <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                                  <span>{outcome}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          {/* Right: Action */}
                          <div className="flex-shrink-0 flex flex-col items-center justify-center gap-3 sm:pl-4 sm:border-l sm:border-slate-100">
                            <div className="text-center">
                              <p className="text-2xl font-bold text-navy-700">{course.duration}</p>
                              <p className="text-xs text-slate-500">{course.level}</p>
                            </div>
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedVideo(course);
                              }}
                              className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-lg shadow-emerald-500/25"
                            >
                              <Play className="w-4 h-4 mr-2 fill-white" />
                              Watch Now
                            </Button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {selectedVideo && (
        <VideoModal 
          video={selectedVideo} 
          onClose={() => setSelectedVideo(null)} 
        />
      )}

      {/* Get Started Section - Essential Beginner Guides */}
      <section className="py-16 bg-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-emerald-100 text-emerald-700 mb-3">
              <BookOpen className="w-3 h-3 mr-1" />
              Start Here
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy-700 mb-3">
              New to Investing? Start Here
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Complete beginner? No worries. We&apos;ll explain everything from scratch, starting with the absolute basics.
            </p>
          </div>

          {/* Absolute Beginner Section */}
          <div className="mb-12">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
              <h3 className="text-xl font-heading font-bold text-navy-700">First Things First</h3>
            </div>
            <Card className="bg-white border-2 border-emerald-200 shadow-lg overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-emerald-500 to-emerald-600" />
              <CardContent className="p-8">
                <Badge className="bg-emerald-100 text-emerald-700 mb-4">Start Here</Badge>
                <h3 className="font-heading font-bold text-2xl text-navy-700 mb-4">
                  What is a Brokerage Account? (And Why You Need One)
                </h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Before you can buy any stocks or ETFs, you need a place to buy them. That&apos;s what a brokerage account is. 
                  Think of it like opening a bank account, but instead of just holding cash, it lets you buy investments.
                </p>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-navy-700 mb-2">What is a Brokerage?</h4>
                    <p className="text-sm text-slate-600">
                      A brokerage is a company that acts as a middleman between you and the stock market. 
                      They handle all the technical stuff so you can easily buy and sell investments with a few clicks.
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-navy-700 mb-2">Why Do I Need One?</h4>
                    <p className="text-sm text-slate-600">
                      You can&apos;t buy stocks directly from companies. A brokerage account gives you access to the stock market, 
                      lets you hold your investments safely, and tracks everything for tax purposes.
                    </p>
                  </div>
                </div>

                <div className="bg-emerald-50 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-navy-700 mb-3">Popular Canadian Brokerages for Beginners:</h4>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="font-medium text-emerald-700">Wealthsimple Trade</p>
                      <p className="text-xs text-slate-500">No commission fees, great app</p>
                    </div>
                    <div className="text-center">
                      <p className="font-medium text-emerald-700">Questrade</p>
                      <p className="text-xs text-slate-500">Free ETF purchases, established</p>
                    </div>
                    <div className="text-center">
                      <p className="font-medium text-emerald-700">TD Direct Investing</p>
                      <p className="text-xs text-slate-500">Big bank security, more tools</p>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-slate-500 italic">
                  Opening an account is free and takes about 10 minutes. You&apos;ll need your SIN number and some ID.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Next Steps Section */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
              <h3 className="text-xl font-heading font-bold text-navy-700">Essential Reading</h3>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* TFSA vs RRSP */}
            <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-shadow overflow-hidden group">
              <div className="h-2 bg-gradient-to-r from-blue-500 to-blue-600" />
              <CardContent className="p-6">
                <Badge className="bg-blue-100 text-blue-700 mb-3">Must Read</Badge>
                <h3 className="font-heading font-bold text-xl text-navy-700 mb-2 group-hover:text-emerald-600 transition-colors">
                  TFSA vs RRSP: Which One First?
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  The ultimate comparison for students. Learn when to use each account and which one makes sense for your situation.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">5 min read</span>
                  <Link to="/blog/tfsa-101-students-guide" className="text-emerald-600 text-sm font-medium hover:underline flex items-center gap-1">
                    Read Guide <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* TFSA Starter Pack */}
            <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-shadow overflow-hidden group">
              <div className="h-2 bg-gradient-to-r from-emerald-500 to-emerald-600" />
              <CardContent className="p-6">
                <Badge className="bg-emerald-100 text-emerald-700 mb-3">Beginner</Badge>
                <h3 className="font-heading font-bold text-xl text-navy-700 mb-2 group-hover:text-emerald-600 transition-colors">
                  TFSA Contribution Room 2026
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  Everything you need to know about your TFSA contribution room. Step-by-step guide with examples.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">7 min read</span>
                  <Link to="/blog/mastering-your-tfsa-contribution-room-calculation-for-2026" className="text-emerald-600 text-sm font-medium hover:underline flex items-center gap-1">
                    Read Guide <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* First Investment */}
            <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-shadow overflow-hidden group">
              <div className="h-2 bg-gradient-to-r from-purple-500 to-purple-600" />
              <CardContent className="p-6">
                <Badge className="bg-purple-100 text-purple-700 mb-3">Popular</Badge>
                <h3 className="font-heading font-bold text-xl text-navy-700 mb-2 group-hover:text-emerald-600 transition-colors">
                  Your First Investment Guide
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  A practical roadmap for making your first investment as a Canadian student.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">6 min read</span>
                  <Link to="/blog/first-investment-beginners-guide" className="text-emerald-600 text-sm font-medium hover:underline flex items-center gap-1">
                    Read Guide <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* ETF Basics */}
            <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-shadow overflow-hidden group">
              <div className="h-2 bg-gradient-to-r from-orange-500 to-orange-600" />
              <CardContent className="p-6">
                <Badge className="bg-orange-100 text-orange-700 mb-3">Essential</Badge>
                <h3 className="font-heading font-bold text-xl text-navy-700 mb-2 group-hover:text-emerald-600 transition-colors">
                  How to Buy ETFs in Canada
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  Step-by-step guide for complete beginners. Simple, diversified, low-cost investing.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">4 min read</span>
                  <Link to="/blog/how-to-buy-etfs-in-canada-step-by-step-guide-for-complete-be" className="text-emerald-600 text-sm font-medium hover:underline flex items-center gap-1">
                    Read Guide <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Emergency Fund */}
            <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-shadow overflow-hidden group">
              <div className="h-2 bg-gradient-to-r from-teal-500 to-teal-600" />
              <CardContent className="p-6">
                <Badge className="bg-teal-100 text-teal-700 mb-3">Foundation</Badge>
                <h3 className="font-heading font-bold text-xl text-navy-700 mb-2 group-hover:text-emerald-600 transition-colors">
                  Building Your Emergency Fund
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  Before you invest, you need a safety net. Learn how much to save and where to keep it.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">5 min read</span>
                  <Link to="/blog/building-your-emergency-fund-a-canadian-student-s-guide" className="text-emerald-600 text-sm font-medium hover:underline flex items-center gap-1">
                    Read Guide <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Budget Guide */}
            <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-shadow overflow-hidden group">
              <div className="h-2 bg-gradient-to-r from-red-500 to-red-600" />
              <CardContent className="p-6">
                <Badge className="bg-red-100 text-red-700 mb-3">Important</Badge>
                <h3 className="font-heading font-bold text-xl text-navy-700 mb-2 group-hover:text-emerald-600 transition-colors">
                  50/30/20 Budget for Students
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  Make budgeting work on a student income. Practical tips that actually stick.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">5 min read</span>
                  <Link to="/blog/50/30/20-budget-rule-for-students-make-it-work-on-your-canad" className="text-emerald-600 text-sm font-medium hover:underline flex items-center gap-1">
                    Read Guide <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white sticky top-16 z-40 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Section Title */}
            <h2 className="text-xl font-heading font-bold text-navy-700 hidden md:block">
              📚 Articles
            </h2>
            
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={activeCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActiveCategory(category)}
                  className={
                    activeCategory === category
                      ? "bg-navy-700 hover:bg-navy-600"
                      : "hover:bg-slate-100"
                  }
                  data-testid={`filter-${category.toLowerCase().replace(" ", "-")}`}
                >
                  {category}
                </Button>
              ))}
            </div>

            {/* Search */}
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="blog-search-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse">
                  <div className="h-48 bg-slate-200" />
                  <CardContent className="pt-4 space-y-3">
                    <div className="h-4 bg-slate-200 rounded w-1/4" />
                    <div className="h-6 bg-slate-200 rounded" />
                    <div className="h-4 bg-slate-200 rounded w-3/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg">No articles found.</p>
              <Button
                variant="link"
                onClick={() => {
                  setActiveCategory("All");
                  setSearchQuery("");
                }}
                className="text-emerald-600"
              >
                Clear filters
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/blog/${post.slug}`}>
                    <Card className="h-full card-hover border-0 shadow-card overflow-hidden bg-white">
                      {post.image_url && (
                        <img
                          src={post.image_url}
                          alt={post.title}
                          className="w-full h-48 object-cover"
                        />
                      )}
                      <CardContent className="pt-4">
                        <Badge variant="secondary" className="mb-2">
                          {post.category}
                        </Badge>
                        <h2 className="font-heading font-bold text-xl text-navy-700 mb-2 line-clamp-2">
                          {cleanContent(post.title)}
                        </h2>
                        <p className="text-slate-600 text-sm line-clamp-3 mb-4">
                          {cleanContent(post.excerpt)}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-400">
                            {new Date(post.created_at).toLocaleDateString(
                              "en-US",
                              { month: "short", day: "numeric", year: "numeric" }
                            )}
                          </span>
                          <span className="text-emerald-600 text-sm font-medium flex items-center">
                            Read more
                            <ArrowRight className="w-4 h-4 ml-1" />
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-12 bg-gradient-to-br from-navy-700 to-navy-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Mail className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
          <h2 className="text-2xl font-heading font-bold text-white mb-3">
            Join Our Newsletter
          </h2>
          <p className="text-slate-300 mb-2 max-w-xl mx-auto">
            Be the first to know when we host sessions, launch courses, and release more amazing financial tools.
          </p>
          <p className="text-emerald-400 text-sm mb-6">
            <Sparkles className="w-4 h-4 inline mr-1" />
            Newsletter subscribers get exclusive early-bird discounts!
          </p>
          
          {newsletterSuccess ? (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-emerald-500/20 rounded-xl p-4 max-w-sm mx-auto"
            >
              <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-white font-medium">You&apos;re on the list!</p>
              <p className="text-emerald-200 text-sm">We&apos;ll notify you about new blogs, market updates, content, and AI tools!</p>
            </motion.div>
          ) : (
            <form 
              onSubmit={async (e) => {
                e.preventDefault();
                if (!newsletterEmail) {
                  toast.error("Please enter your email");
                  return;
                }
                setNewsletterLoading(true);
                try {
                  await axios.post(`${API_URL}/api/subscribers`, {
                    email: newsletterEmail,
                    phone: "",
                    source: "newsletter_blog",
                  });
                  setNewsletterSuccess(true);
                  toast.success("Welcome to the Compoundr newsletter! 🎉");
                } catch (error) {
                  if (error.response?.status === 400) {
                    toast.success("You're already subscribed!");
                    setNewsletterSuccess(true);
                  } else {
                    toast.error("Something went wrong");
                  }
                } finally {
                  setNewsletterLoading(false);
                }
              }} 
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <Input
                type="email"
                placeholder="Enter your email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="flex-1 h-11 bg-white/10 border-white/20 text-white placeholder:text-slate-400"
              />
              <Button
                type="submit"
                disabled={newsletterLoading}
                className="h-11 bg-emerald-500 hover:bg-emerald-600 text-white px-6"
              >
                {newsletterLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Subscribe"}
              </Button>
            </form>
          )}
        </div>
      </section>

      
      {/* Wealthsimple Steps Modal */}
      <WealthsimpleStepsModal 
        open={wealthsimpleModalOpen} 
        onOpenChange={setWealthsimpleModalOpen} 
      />
    </div>
  );
};

export default Blog;
